import get from 'lodash/get';
import head from 'lodash/head';
import last from 'lodash/last';
import cloneDeep from 'lodash/cloneDeep';
import type { GroupLayout } from '../../types/form/Layout';
import type { Slot } from '../../types/form/Slot';

import {
  TYPE_UPLOAD_FIELD,
  TYPE_TEXT_FIELD,
  TYPE_LONG_TEXT_FIELD,
  TYPE_SELECT_FIELD,
  TYPE_TAXONOMY_FIELD,
  TYPE_DATE_FIELD,
  ELEMENT_DESCRIPTION_ID,
  ELEMENT_EMAIL_ID,
  BOTTOM_DISPLACEMENT_THRESHOLD,
  TOP_DISPLACEMENT_THRESHOLD,
  SCROLL_INCREMENT,
  TYPE_RADIO_FIELD,
  TYPE_CHECKBOX_FIELD,
  TYPE_NUMBER_FIELD,
  TYPE_EMAIL_ADDRESS_FIELD,
} from '../../constants';

const TOTAL_COL = 2;
const FULL_COL_WIDTH = TOTAL_COL;
const ONE_COL_WIDTH = TOTAL_COL / 2;
const LAYOUT_MARGIN = [10, 0];
const CONTAINER_PADDING = [0, 24];
const Y_MARGIN = 28;
const TOTAL_BORDER_WIDTH = 4;

// We should delete these hardcoded mappings and use the actual content height
// Height values of 137, 198, and 385 were calculated specifically for File Request
// to fix a defect where the grid height was incorrect during insertion.
// Ticket to remove this hardcoded mapping: https://jira.inside-box.net/browse/FORMS-11995
const componentIdToHeight = {
  [ELEMENT_EMAIL_ID]: 137,
  [ELEMENT_DESCRIPTION_ID]: 201,
} as const;
const componentTypeToHeight = {
  [TYPE_UPLOAD_FIELD]: 396,
  [TYPE_TEXT_FIELD]: 148,
  [TYPE_DATE_FIELD]: 148,
  [TYPE_SELECT_FIELD]: 148,
  [TYPE_TAXONOMY_FIELD]: 148,
  [TYPE_LONG_TEXT_FIELD]: 188,
  [TYPE_RADIO_FIELD]: 138,
  [TYPE_CHECKBOX_FIELD]: 138,
  [TYPE_NUMBER_FIELD]: 148,
  [TYPE_EMAIL_ADDRESS_FIELD]: 148,
} as const;

const itemRowHeight = (
  itemId: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  component: any,
  isDescriptionEditable: boolean = false,
): number => {
  let componentHeight = get(componentIdToHeight, itemId, 0);
  if (!componentHeight) {
    componentHeight = get(componentTypeToHeight, component.type, 0);
  }
  if (isDescriptionEditable) {
    componentHeight += 31;
  }
  return componentHeight;
};

const getFormElementKey = (groupId: string, id: string) =>
  `grid-layout-${groupId}-${id}`;

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const isItemInTwoColumn = (item: any | null): boolean =>
  !!(item && item.w === ONE_COL_WIDTH);

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const isAtTwoColumnLeft = (item: any | null): boolean =>
  !!(item && item.x === 0);

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const isAtTwoColumnRight = (item: any | null): boolean =>
  !!(item && item.x === ONE_COL_WIDTH);

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const isTwoItemOnSameRow = (item1: any | null, item2: any | null) =>
  item1?.y === item2?.y;

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */ // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
const isFirstItem = (layout: any, itemId: string) => head(layout)?.i === itemId;

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */ // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
const isLastItem = (layout: any, itemId: string) => last(layout)?.i === itemId;
/**
 * function for find all empty slots (spaces) within two column layouts
 * if no slot exists find return empty list
 * @param {GroupLayout} layout used in react grid
 * @returns list of slot object
 */

const getTwoColumnSlotsInLayout = (layout: GroupLayout): Array<Slot> => {
  const slots: Array<Slot> = [];
  let index = 0;
  // find and add all slots in the list
  while (index < layout.length) {
    const curItem = layout[index];
    const nextItem = layout[index + 1];
    if (!isItemInTwoColumn(curItem)) {
      index += 1;
    } else if (isAtTwoColumnLeft(curItem)) {
      const slot = {
        x: ONE_COL_WIDTH,
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        y: curItem.y,
        w: ONE_COL_WIDTH,
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        h: curItem.h,
        insertIndex: index + 1,
      } as const;
      // check if it is last item
      if (index === layout.length - 1) {
        slots.push(slot);
        index += 1;
      } else if (!isItemInTwoColumn(nextItem)) {
        slots.push(slot);
        index += 2;
      } else if (
        isItemInTwoColumn(nextItem) &&
        !isTwoItemOnSameRow(curItem, nextItem)
      ) {
        slots.push(slot);
        index += 1;
      } else if (
        isItemInTwoColumn(nextItem) &&
        isTwoItemOnSameRow(curItem, nextItem)
      ) {
        index += 2;
      }
    } else if (isAtTwoColumnRight(curItem)) {
      slots.push({
        x: 0,
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        y: curItem.y,
        w: ONE_COL_WIDTH,
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        h: curItem.h,
        insertIndex: index,
      });
      index += 1;
    }
  }
  return slots;
};
/**
 * function: get new layout after insert a field
 * @param {GroupLayout} layout
 * @param {string} fieldType
 * @param {number} insertIndex
 * @param {string} newUniqueKey field unique id
 * @param {number} colW column width of the field Optional default 2
 * @param {number} colX column starting X position Optional default 0
 * @param {number} componentHeight Optional default 0
 * @param {boolean} isDescriptionEditable Optional default false
 * @returns new layout
 */
const getLayoutAfterInsertField = (
  layout: GroupLayout,
  fieldType: string,
  insertIndex: number,
  newUniqueKey: string,
  colW: number = FULL_COL_WIDTH,
  colX: number = 0,
  componentHeight: number = 0,
  isDescriptionEditable: boolean = false,
): GroupLayout => {
  const isTwoColumnInsert = colW === ONE_COL_WIDTH;
  if (!layout) return layout;
  const newLayout = cloneDeep(layout);
  const h =
    componentHeight ||
    itemRowHeight(fieldType, { type: fieldType }, isDescriptionEditable);
  let newItemLayout;
  // append new field to last of the layout
  if (insertIndex === layout.length) {
    const lastItem = last(newLayout);
    // if lastItem is undefined the newItem should be the first one so y = 0
    // if not use lastItem.y to infer newItem y value
    let y = 0;
    if (lastItem) {
      y = isTwoColumnInsert ? lastItem.y : lastItem.y + lastItem.h;
    }
    newItemLayout = {
      ...lastItem,
      i: newUniqueKey,
      h,
      y,
      w: colW,
      x: colX,
    };
    newLayout.push(newItemLayout);
    return newLayout;
  }
  const itemBefore = newLayout[insertIndex - 1];
  newItemLayout = {
    i: newUniqueKey,
    y:
      // check for insert right two column slot
      isTwoColumnInsert &&
      isItemInTwoColumn(itemBefore) &&
      isAtTwoColumnLeft(itemBefore)
        ? // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
          itemBefore.y
        : // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
          newLayout[insertIndex].y,
    h,
    w: colW,
    x: colX,
  };
  // Insert field and update items y pos after the field
  newLayout.splice(insertIndex, 0, newItemLayout);
  if (!isTwoColumnInsert) {
    for (let pos = insertIndex + 1; pos < newLayout.length; pos += 1) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
      newLayout[pos].y += h;
    }
  }
  return newLayout;
};

const getLayoutAfterInsertFieldList = (
  layout: GroupLayout,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  newComponents: any,
  insertIndex: number,
  newUniqueKeys: string[],
): GroupLayout => {
  let newLayout = layout;
  for (let i = newUniqueKeys.length - 1; i >= 0; i -= 1) {
    const newUniqueKey = newUniqueKeys[i];
    newLayout = getLayoutAfterInsertField(
      newLayout,
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
      newComponents[newUniqueKey].type,
      insertIndex,
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
      newUniqueKey,
      // default value for colW
      FULL_COL_WIDTH,
      // default value for colX
      0,
      // default value for componentHeight
      0,
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2538
      !!newComponents[newUniqueKey]?.description,
    );
  }
  return newLayout;
};

const getLayoutAfterFieldMove = (
  layout: GroupLayout,
  fromIdx: number,
  toIdx: number,
): GroupLayout => {
  const newLayout = cloneDeep(layout);
  const element = newLayout[fromIdx];
  newLayout.splice(fromIdx, 1);
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
  newLayout.splice(toIdx, 0, element);
  if (toIdx < fromIdx) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
    element.y = layout[toIdx].y;
    for (let pos = toIdx + 1; pos <= fromIdx; pos += 1) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
      newLayout[pos].y += element.h;
    }
  } else {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
    element.y = layout[toIdx].y + layout[toIdx].h - element.h;
    for (let pos = fromIdx; pos < toIdx; pos += 1) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
      newLayout[pos].y -= element.h;
    }
  }
  return newLayout;
};

const moveRightField = (layout: GroupLayout, id: string): GroupLayout => {
  const newLayout = cloneDeep(layout);
  const fromIdx = layout.findIndex(item => item.i === id);
  const nextIdx = fromIdx + 1;

  const [currItem, nextItem] = [newLayout[fromIdx], newLayout[nextIdx] ?? {}];

  if (!currItem) {
    return newLayout;
  }

  if (
    !isItemInTwoColumn(nextItem) ||
    (isItemInTwoColumn(nextItem) && !isTwoItemOnSameRow(currItem, nextItem))
  ) {
    currItem.x = ONE_COL_WIDTH;
  } else if (
    isItemInTwoColumn(nextItem) &&
    isTwoItemOnSameRow(currItem, nextItem)
  ) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
    nextItem.x = currItem?.x;
    currItem.x = ONE_COL_WIDTH;
  }
  return newLayout;
};

const moveLeftField = (layout: GroupLayout, id: string): GroupLayout => {
  const newLayout = cloneDeep(layout);
  const fromIdx = layout.findIndex(item => item.i === id);
  const lastIdx = fromIdx - 1;
  const [currItem, lastItem] = [newLayout[fromIdx], newLayout[lastIdx]];
  if (
    !isItemInTwoColumn(lastItem) ||
    (isItemInTwoColumn(lastItem) && !isTwoItemOnSameRow(currItem, lastItem))
  ) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
    currItem.x = 0;
  } else if (
    isItemInTwoColumn(lastItem) &&
    isTwoItemOnSameRow(currItem, lastItem)
  ) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
    lastItem.x = currItem.x;
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
    currItem.x = 0;
  }
  return newLayout;
};

const moveUpField = (layout: GroupLayout, id: string): GroupLayout => {
  const fromIdx = layout.findIndex(item => item.i === id);
  let toIndex = fromIdx - 1;
  if (
    isItemInTwoColumn(layout[toIndex]) &&
    isItemInTwoColumn(layout[toIndex - 1]) &&
    isTwoItemOnSameRow(layout[toIndex], layout[toIndex - 1])
  ) {
    toIndex = fromIdx - 2;
  }
  return getLayoutAfterFieldMove(layout, fromIdx, toIndex);
};

const moveDownField = (layout: GroupLayout, id: string): GroupLayout => {
  const fromIdx = layout.findIndex(item => item.i === id);
  let toIndex = fromIdx + 1;
  if (
    isItemInTwoColumn(layout[toIndex]) &&
    isItemInTwoColumn(layout[toIndex + 1]) &&
    isTwoItemOnSameRow(layout[toIndex], layout[toIndex + 1])
  ) {
    toIndex = fromIdx + 2;
  }
  return getLayoutAfterFieldMove(layout, fromIdx, toIndex);
};

const getLayoutAfterFieldColToggle = (
  layout: GroupLayout,
  id: string,
): GroupLayout => {
  const newLayout = cloneDeep(layout);
  const fieldIdx = layout.findIndex(item => item.i === id);
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
  newLayout[fieldIdx].w =
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
    newLayout[fieldIdx].w === FULL_COL_WIDTH ? ONE_COL_WIDTH : FULL_COL_WIDTH;
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
  newLayout[fieldIdx].x = 0;
  return newLayout;
};
/**
 * function for update Field Height in two column
 */
const updateTwoColItemHeightInLayout = (
  layout: GroupLayout,
  itemIndex: number,
  prevHeight: number,
  newHeight: number,
  groupId: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  elementHeightMapper: any = {},
) => {
  const curItem = layout[itemIndex];
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
  curItem.h = newHeight;
  const sameRowFieldIndex = isAtTwoColumnLeft(curItem)
    ? itemIndex + 1
    : itemIndex - 1;
  const sameRowFieldItem = layout[sameRowFieldIndex];
  if (
    !sameRowFieldItem ||
    !isItemInTwoColumn(sameRowFieldItem) ||
    !isTwoItemOnSameRow(curItem, sameRowFieldItem)
  ) {
    return;
  }
  const sameRowFieldElementHeight =
    elementHeightMapper[getFormElementKey(groupId, sameRowFieldItem.i)];

  // when current field size decrease also update same row field height
  if (newHeight < prevHeight && sameRowFieldElementHeight) {
    sameRowFieldItem.h =
      // if the udpated new height shorter than same row element height set layout item to element height
      newHeight > sameRowFieldElementHeight
        ? newHeight
        : sameRowFieldElementHeight;
  }
};

const updateItemHeightInLayout = (
  layout: GroupLayout,
  itemIndex: number,
  newHeight: number,
) => {
  const curItem = layout[itemIndex];
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
  const diffH = newHeight - curItem.h;
  // ignore height change due to on focus border display
  if (Math.abs(diffH) <= TOTAL_BORDER_WIDTH) return;
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
  curItem.h = newHeight;
  for (let index = itemIndex + 1; index < layout.length; index += 1) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2532
    layout[index].y += diffH;
  }
};

/**
 * function to ensure two columns within one row of fields
 * when fields have different height.
 * check for height update for one col field
 * @param {GroupLayout} layout
 * @param {string} groupId
 * @param {object} elementHeightMapper
 * @returns newlayout
 */
const resizeLayoutItemHeight = (
  layout: GroupLayout,
  groupId: string,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  elementHeightMapper: any = {},
): GroupLayout => {
  const newLayout = cloneDeep(layout);
  let index = 0;
  while (index < layout.length) {
    const curItem = newLayout[index];
    const nextItem = newLayout[index + 1];
    if (
      isItemInTwoColumn(curItem) &&
      isItemInTwoColumn(nextItem) &&
      isTwoItemOnSameRow(curItem, nextItem)
    ) {
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
      if (curItem.h !== nextItem.h) {
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        const rowHeight = Math.max(curItem.h, nextItem.h);
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        curItem.h = rowHeight;
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        nextItem.h = rowHeight;
      }
      index += 2;
    } else {
      const elementHeight =
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
        elementHeightMapper[getFormElementKey(groupId, curItem.i)];
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18048
      if (curItem.h && elementHeight && curItem.h !== elementHeight) {
        updateItemHeightInLayout(newLayout, index, elementHeight);
      }
      index += 1;
    }
  }
  return newLayout;
};

/**
 * function to enable scrolling for FR builder drag and drop
 * scrolls when mouse position goes above/below a threshold
 * @param {MouseEvent} event mouse move event
 * @param {HTMLElement} container container to scroll
 * @returns callback to scroll container if needed
 */
const animateScrollbar = ({ clientY }: MouseEvent, container: HTMLElement) => {
  let direction = 0;

  if (clientY < TOP_DISPLACEMENT_THRESHOLD) {
    direction = -SCROLL_INCREMENT;
  } else if (window.innerHeight - clientY < BOTTOM_DISPLACEMENT_THRESHOLD) {
    direction = SCROLL_INCREMENT;
  }

  if (direction) {
    return () => {
      return setInterval(() => {
        window.requestAnimationFrame(() => {
          container.scroll(0, container.scrollTop + direction);
        });
      }, 1);
    };
  }
  return () => {
    return null;
  };
};

/**
 * function to return dropIndex when duplicating a field
 * @param {GroupLayout} layout
 * @param {String} id id of the field to be duplicated
 * @returns {Object} - An object containing:
 *   - itemIndex: The original index of the item to be duplicated
 *   - dropIndex: The index where the duplicate should be placed
 */
const getDropIndexForDuplicateField = (
  layout: GroupLayout,
  id: string,
): {
  dropIndex: number;
  itemIndex: number;
} => {
  if (layout === undefined) {
    return { itemIndex: 0, dropIndex: 0 };
  }
  const itemIndex = layout.findIndex(item => item.i === id);
  const item = layout[itemIndex];
  const nextItem = layout[itemIndex + 1];

  if (
    isItemInTwoColumn(item) &&
    isAtTwoColumnLeft(item) &&
    isAtTwoColumnRight(nextItem)
  ) {
    return { itemIndex, dropIndex: itemIndex + 2 };
  }
  return { itemIndex, dropIndex: itemIndex + 1 };
};

export {
  animateScrollbar,
  componentIdToHeight,
  componentTypeToHeight,
  itemRowHeight,
  getFormElementKey,
  updateItemHeightInLayout,
  updateTwoColItemHeightInLayout,
  isFirstItem,
  isLastItem,
  isItemInTwoColumn,
  isAtTwoColumnLeft,
  isAtTwoColumnRight,
  isTwoItemOnSameRow,
  getTwoColumnSlotsInLayout,
  getLayoutAfterInsertField,
  getLayoutAfterInsertFieldList,
  getLayoutAfterFieldColToggle,
  moveRightField,
  moveLeftField,
  moveUpField,
  moveDownField,
  resizeLayoutItemHeight,
  getDropIndexForDuplicateField,
  TOTAL_COL,
  FULL_COL_WIDTH,
  ONE_COL_WIDTH,
  LAYOUT_MARGIN,
  CONTAINER_PADDING,
  Y_MARGIN,
  TOTAL_BORDER_WIDTH,
};
